import { gql, useMutation, useQuery } from "@apollo/client";
import { Alert, Button, Card, Form, Layout, Menu, Row, Space, Typography } from "antd";
import { CenterResult } from "components/CenterResult";
import { CenterSpinner } from "components/CenterSpinner";
import { camelCase } from "lodash";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate, useParams } from "react-router-dom";

import { UserBrandForm } from "./Form";

const accountUserBrandQuery = gql`
  query (
    $id: ID!
    $pagination: ConnectionPagination
    $collectionBrandSorting: CollectionBrandSorting
    $accountUserSorting: AccountUserSorting
    $searchQuery: String
  ) {
    accountUserBrand(id: $id) {
      id
      user {
        id
        code
        email
      }
      brand {
        id
        code
        nameEn
        nameIt
      }
      salesperson {
        id
        code
        email
      }
    }
    collectionBrands(pagination: $pagination, sorting: $collectionBrandSorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
    accountUsers(pagination: $pagination, sorting: $accountUserSorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        email
        role
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const updateAccountUserBrandMutation = gql`
  mutation ($id: ID!, $input: UpdateAccountUserBrandInput!) {
    updateAccountUserBrand(id: $id, input: $input) {
      userBrand {
        id
        user {
          id
          code
        }
        brand {
          id
          code
        }
        salesperson {
          id
          code
        }
      }
    }
  }
`;

interface FieldData {
  name: string;
  errors: string[];
}

export const Edit: React.FC<{}> = () => {
  const [form] = Form.useForm();
  const [otherErrors, setOtherErrors] = useState<string[]>([]);
  const navigate = useNavigate();
  const { id, userId } = useParams();

  const backPath = () => {
    return userId ? "../../../../users/" + userId : "../..";
  };

  const {
    error,
    loading: queryLoading,
    data,
  } = useQuery(accountUserBrandQuery, {
    variables: { id },
    fetchPolicy: "cache-and-network",
  });

  const [execMutation, { loading }] = useMutation(updateAccountUserBrandMutation, {
    errorPolicy: "none",
    onError: ({ graphQLErrors }) => {
      const fieldsErrors: FieldData[] = [];
      const otherErrors: string[] = [];
      graphQLErrors?.forEach((graphQLError) => {
        if (graphQLError.name) {
          fieldsErrors.push({ name: camelCase(graphQLError.name), errors: (graphQLError as any).messages });
        } else {
          otherErrors.push(graphQLError.message);
        }
      });
      form.setFields(fieldsErrors);
      setOtherErrors(otherErrors);
    },
    onCompleted: (data) => {
      navigate(backPath());
    },
  });

  const onFinish = (values: any) => {
    const input = {
      ...values,
      salespersonId: values.salespersonId ?? null,
    };
    execMutation({ variables: { id, input } });
  };

  if (queryLoading) return <CenterSpinner size="large" />;
  if (error)
    return (
      <CenterResult
        status="404"
        title="404"
        subTitle="Sorry, the page you visited does not exist."
        extra={
          <NavLink to={backPath()}>
            <Button type="primary">Back to list</Button>
          </NavLink>
        }
      />
    );

  const values = {
    ...data?.accountUserBrand,
    userId: data?.accountUserBrand?.user?.id,
    brandId: data?.accountUserBrand?.brand?.id,
    salespersonId: data?.accountUserBrand?.salesperson?.id,
  };

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border">
              <Typography.Title level={4} style={{ lineHeight: "auto" }}>
                Edit User Brand: {data?.accountUserBrand.user.email}
              </Typography.Title>
            </Menu.Item>
          </Menu>
          <Menu theme="light" mode="horizontal">
            <Menu.Item className="no-border no-margin">
              <NavLink to={backPath()}>
                <Button>Cancel</Button>
              </NavLink>
            </Menu.Item>
            <Menu.Item className="no-border">
              <Button
                type="primary"
                loading={loading}
                onClick={() => {
                  form.submit();
                }}
              >
                Save
              </Button>
            </Menu.Item>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <Layout.Content className="content">
          <Card>
            {otherErrors.length > 0 && (
              <Space direction="vertical" style={{ marginBottom: "10px" }}>
                {otherErrors.map((otherError, index) => (
                  <Alert key={index} type="error" message="Error" description={otherError} />
                ))}
              </Space>
            )}

            <UserBrandForm form={form} values={values} handler={onFinish} data={data} />
          </Card>
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
